/**
 * Program Category Configuration — v3 Brand & UX Redesign
 *
 * Maps program titles (from API) to simplified display names, benefit-driven subtitles,
 * category colors, icons, and sort order. This provides the visual identity layer
 * without requiring backend changes.
 *
 * Design spec: Cumulus handoff — NovaMed Programs Brand & UX Redesign v3
 * @see https://www.notion.so/NovaMed-Programs-Brand-UX-Redesign-v3-Handoff-Cumulus-Stratus-322d6bca6a8e81bf9f9fcc06ad1caff1
 */

export interface ProgramCategoryConfig {
  /** Simplified display name shown as card title */
  displayName: string;
  /** TM brand name shown as sub-label */
  brandName: string;
  /** Benefit-driven subtitle for patients */
  subtitle: string;
  /** Category color (hex) for card accent, icon bg, badges */
  color: string;
  /** Lighter variant for backgrounds */
  colorLight: string;
  /** Text color that contrasts well on the light bg */
  colorText: string;
  /** Lucide icon name */
  icon: string;
  /** Sort priority (lower = first) */
  sortOrder: number;
  /** Category label */
  category: string;
}

/**
 * Mapping keyed by normalized program title (lowercased, trimmed).
 * Matches against the `title` field from the API.
 */
const PROGRAM_CATEGORY_MAP: Record<string, ProgramCategoryConfig> = {
  // Weight Management
  'metabolift™ - weightloss -...': {
    displayName: 'Weight Management',
    brandName: 'MetaboLift™',
    subtitle: 'Personalized GLP-1 programs. Real results, real support.',
    color: '#FF6B6B',
    colorLight: '#FF6B6B1A',
    colorText: '#B91C1C',
    icon: 'Scale',
    sortOrder: 1,
    category: 'Weight',
  },
  // Hormone Therapy
  'vitalityreset™': {
    displayName: 'Hormone Therapy',
    brandName: 'VitalityReset™',
    subtitle: 'Rebalance hormones. Restore energy and vitality.',
    color: '#F4A261',
    colorLight: '#F4A2611A',
    colorText: '#92400E',
    icon: 'FlaskConical',
    sortOrder: 2,
    category: 'Hormones',
  },
  // Longevity & Anti-Aging
  'agewell360™': {
    displayName: 'Longevity & Anti-Aging',
    brandName: 'AgeWell360™',
    subtitle: 'Science-backed protocols for aging on your terms.',
    color: '#2A9D8F',
    colorLight: '#2A9D8F1A',
    colorText: '#065F53',
    icon: 'Hourglass',
    sortOrder: 3,
    category: 'Longevity',
  },
  // Cognitive Health
  'focusforward™': {
    displayName: 'Cognitive Health',
    brandName: 'FocusForward™',
    subtitle: 'Sharpen focus. Boost mental clarity and memory.',
    color: '#3B82F6',
    colorLight: '#3B82F61A',
    colorText: '#1E40AF',
    icon: 'Brain',
    sortOrder: 4,
    category: 'Cognitive',
  },
  // Immune Support
  'immuneguard™': {
    displayName: 'Immune Support',
    brandName: 'ImmuneGuard™',
    subtitle: "Strengthen your body's natural defenses.",
    color: '#10B981',
    colorLight: '#10B9811A',
    colorText: '#065F46',
    icon: 'ShieldPlus',
    sortOrder: 5,
    category: 'Immune',
  },
  // Performance & Recovery
  'performanceedge™': {
    displayName: 'Performance & Recovery',
    brandName: 'PerformanceEdge™',
    subtitle: 'Optimize performance. Accelerate recovery.',
    color: '#8B5CF6',
    colorLight: '#8B5CF61A',
    colorText: '#5B21B6',
    icon: 'Zap',
    sortOrder: 6,
    category: 'Performance',
  },
  // Joint & Mobility
  'restorejoint™': {
    displayName: 'Joint & Mobility',
    brandName: 'RestoreJoint™',
    subtitle: 'Move freely. Reduce pain. Rebuild strength.',
    color: '#6366F1',
    colorLight: '#6366F11A',
    colorText: '#3730A3',
    icon: 'Bone',
    sortOrder: 7,
    category: 'Mobility',
  },
};

/**
 * Look up category config for a program by its API title.
 * Uses fuzzy matching: lowercased, trimmed, and checks for partial matches.
 * Returns undefined if no match found (renders with defaults).
 */
export function getProgramCategory(title: string): ProgramCategoryConfig | undefined {
  const normalized = title.toLowerCase().trim();

  // Exact match first
  if (PROGRAM_CATEGORY_MAP[normalized]) {
    return PROGRAM_CATEGORY_MAP[normalized];
  }

  // Partial match — check if the program title starts with or contains a known key
  for (const [key, config] of Object.entries(PROGRAM_CATEGORY_MAP)) {
    // Check if normalized title contains the brand name (without ™)
    const brandCore = config.brandName.replace('™', '').toLowerCase();
    if (normalized.includes(brandCore)) {
      return config;
    }
  }

  return undefined;
}

/**
 * Sort programs by category priority.
 * Unknown programs are sorted to the end alphabetically.
 */
export function sortProgramsByCategory<T extends { title: string }>(programs: T[]): T[] {
  return [...programs].sort((a, b) => {
    const catA = getProgramCategory(a.title);
    const catB = getProgramCategory(b.title);

    // Both have categories — sort by priority
    if (catA && catB) return catA.sortOrder - catB.sortOrder;
    // Only one has category — prioritize the one with category
    if (catA && !catB) return -1;
    if (!catA && catB) return 1;
    // Neither has category — alphabetical
    return a.title.localeCompare(b.title);
  });
}

/** Default fallback config for unmapped programs */
export const DEFAULT_PROGRAM_CONFIG: ProgramCategoryConfig = {
  displayName: '',
  brandName: '',
  subtitle: '',
  color: '#64748B',
  colorLight: '#64748B1A',
  colorText: '#334155',
  icon: 'Package',
  sortOrder: 999,
  category: 'Other',
};

export { PROGRAM_CATEGORY_MAP };
